import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconMenu, IconX, IconChevronRight, IconChevronLeft } from '@tabler/icons-react';
import GarageHeaderProfessional from '../components/GarageHeaderProfessional';
import LotsSidebar from '../components/LotsSidebar';
import CommandPalette from '../components/CommandPalette';
import { useAuth } from '../hooks/useAuth';
import { createScopedLogger } from '../utils/logger.js';
import CelebrationOverlay from '../lots/gamification/vehicles/CelebrationOverlay';
import { useAgentNotifications } from '../hooks/useAgentNotifications';

const log = createScopedLogger('OdvCentral');

export default function OdvCentral({ children, onShowAuth }) {
  useAuth(); // Authentication check
  useAgentNotifications(); // Global: toasts when agent responds on another screen
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    localStorage.getItem('sidebarCollapsed') === 'true'
  );
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);

  // Detect current view for AI Action Tray
  const getCurrentView = () => {
    const pathname = location.pathname.toLowerCase();
    
    // More flexible matching - check for route segments
    if (pathname.includes('/assignments') || pathname.includes('/assignment')) return 'assignments';
    if (pathname.includes('/finance') || pathname.includes('/financial')) return 'finance';
    if (pathname.includes('/clients') || pathname.includes('/client')) return 'clients';
    if (pathname.includes('/valets') || pathname.includes('/valet')) return 'valets';
    if (pathname.includes('/dashboard')) return 'dashboard';
    
    // Default route (/) shows AssignmentCenter, so default to 'assignments'
    // This handles the case where user lands on / and AssignmentCenter is rendered
    if (pathname === '/' || pathname === '') return 'assignments';
    
    return null;
  };

  const currentView = getCurrentView();

  // DEBUG: Log current view for AI Action Tray visibility - DISABLED
  // useEffect(() => {
  //   console.log('🎯 [OdvCentral] Current view detection:', {
  //     currentView,
  //     pathname: location.pathname,
  //     willShowTray: !!currentView
  //   });
  //   log.debug?.('Current view changed', { currentView, pathname: location.pathname });
  // }, [currentView, location.pathname]);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Cmd/Ctrl + K for command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
      // Cmd/Ctrl + B for sidebar toggle
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        setSidebarCollapsed(prev => !prev);
      }
      // Escape to close modals
      if (e.key === 'Escape') {
        setShowCommandPalette(false);
        setIsMobileSidebarOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  return (
    <div className="flex h-screen w-screen text-white overflow-hidden bg-black">
      {/* Command Palette - ODV Brain */}
      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
      />

      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Desktop Sidebar */}
      <div className={`hidden md:flex ${
        sidebarCollapsed ? 'w-16' : 'w-48'
      } transition-all duration-300 ease-in-out flex-shrink-0 bg-gradient-to-b from-gray-900 to-black border-r border-gray-800 flex-col h-screen sticky top-0 overflow-hidden`}>
        {/* Logo Area */}
        <div className="h-11 px-2 flex items-center justify-between border-b border-vegas-gold shadow-[0_2px_10px_rgba(174,163,109,0.3)] backdrop-blur-sm flex-shrink-0 bg-black/50">
          <Link to="/" className="group flex items-center py-1" title="Home">
            <img
              src="/odv-logo.png"
              alt="ODV Logo"
              className={`${sidebarCollapsed ? 'h-7' : 'h-8'} w-auto transition-all duration-300 group-hover:brightness-110 cursor-pointer`}
            />
          </Link>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1 text-vegas-gold hover:text-vegas-gold/70 transition-colors"
          >
            {sidebarCollapsed ? <IconChevronRight className="h-4 w-4" /> : <IconChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        {/* Sidebar */}
        <LotsSidebar
          collapsed={sidebarCollapsed}
          onMobileClose={() => {}}
          currentView={currentView}
        />
      </div>

      {/* Mobile Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-44 bg-gradient-to-b from-gray-900 to-black border-r border-gray-800 transform transition-transform duration-300 ease-in-out md:hidden ${
        isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Mobile Close Button */}
        <button
          onClick={() => setIsMobileSidebarOpen(false)}
          className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-white"
        >
          <IconX size={18} />
        </button>

        {/* Logo Area */}
        <div className="h-11 px-3 flex items-center justify-center border-b-2 border-vegas-gold shadow-[0_2px_10px_rgba(174,163,109,0.3)] backdrop-blur-sm flex-shrink-0">
          <Link to="/" className="group" title="Home">
            <img
              src="/odv-logo.png"
              alt="ODV Logo"
              className="h-9 w-auto transition-all duration-300 group-hover:brightness-110 cursor-pointer"
            />
          </Link>
        </div>

        {/* Sidebar */}
        <LotsSidebar
          collapsed={false}
          onMobileClose={() => setIsMobileSidebarOpen(false)}
          currentView={currentView}
        />
      </div>

      {/* Main Content */}
      <div className="flex flex-col flex-grow min-w-0">
        {/* Header */}
        <div className="relative">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileSidebarOpen(true)}
            className="md:hidden absolute left-4 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-white z-20"
          >
            <IconMenu size={24} />
          </button>

          {/* Header */}
          <GarageHeaderProfessional
            onShowAuth={onShowAuth}
            onShowSettings={() => {
              log.debug?.('Header settings clicked - redirecting to profile');
              // Redirect to profile page
              window.location.href = '/profile';
            }}
          />
        </div>

        {/* Main Content Area */}
        <main className="flex-grow overflow-hidden bg-black relative p-0">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/20 via-transparent to-vegas-gold/5 pointer-events-none" />

          {/* Content */}
          <div className="h-full relative z-10">
            <div className="h-full min-w-0 overflow-hidden relative z-10">
              <div className="h-full w-full overflow-y-auto overflow-x-hidden bg-black/90 backdrop-blur-sm">
                {children}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* AI Action Tray moved to LotsSidebar */}

      {/* Gamification Celebrations - Level ups, badges, XP indicators */}
      <CelebrationOverlay />

      {/* Vegas Gold accent */}
      <div className="fixed bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-vegas-gold to-transparent opacity-50" />
    </div>
  );
}
